import { Card, Col, Empty, Progress, Row, Skeleton, Space, Tooltip, Typography } from "antd";
import React from "react";
import LineChart from "./_LineChart";
import { useSelector } from "react-redux";
import { dataAnalyticsDashboardSelector, dataAnalyticsLoadingSelector } from "@/store/dataAnalytics";
import { useTranslation } from "react-i18next";
import styles from "./_styles.less";
import { useSearchParams } from "@/hooks/useSearchParams";
import DashboardBarChart from "./_DashboardBarChart";
import useModulo from "@/hooks/useModulo";
import useLoading from "@/hooks/useLoading";
import { fetchWorkflowResumoByPeriodo } from "@/store/workflow";

const { Text } = Typography;

export default function DashboardCharts() {
  const dadosLoading = useSelector(dataAnalyticsLoadingSelector);
  const workflowLoading = useLoading(fetchWorkflowResumoByPeriodo.typePrefix);
  const dashboardData = useSelector(dataAnalyticsDashboardSelector);
  const { hasModulo } = useModulo();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const modulosLabels = { societario: t("Societário"), regulatorio: t("Regulatório"), compliance: t("Compliance"), consultivo: t("Consultivo") };
  const modulosSelecionados = searchParams.get("modulosNames")?.split(",").filter(Boolean) || [];

  const progressItems =
    dashboardData?.totalizadores?.totalPendenciasModulo
      ?.filter((item) => modulosSelecionados.length === 0 || modulosSelecionados.includes(item.modulo))
      .map((modulo) => {
        return {
          name: modulosLabels[modulo.modulo],
          percent: (modulo.totalEmDia / modulo.totalGeral) * 100 || 0,
          total: modulo.totalGeral || 0,
          emDia: modulo.totalEmDia || 0,
        };
      }) ?? [];

  return (
    <>
      <Row gutter={[8, 8]} className={styles["row-dashboard"]}>
        <Col span={24}>
          <Card title={t("Estatísticas de Trabalho")} className={styles["card-chart"]}>
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              {/* GRAFICO DE EMPRESAS */}
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  <Skeleton loading={dadosLoading} active paragraph={{ rows: 4, width: "100%" }}>
                    <div className={styles["line-chart-container"]}>
                      <LineChart tipoData="empresas" />
                    </div>
                  </Skeleton>
                </Col>
              </Row>

              {/* GRÁFICO DE ATIVIDADES */}
              <Row gutter={[8, 8]}>
                <Col xs={24} lg={16}>
                  <Skeleton loading={dadosLoading} active paragraph={{ rows: 6, width: "100%" }}>
                    <div className={styles["line-chart-container"]}>
                      <LineChart tipoData="alertas" />
                    </div>
                  </Skeleton>
                </Col>
                <Col xs={24} lg={8}>
                  <div className={styles["chart-side-panel"]}>
                    <Card loading={dadosLoading} bordered={false} style={{ height: "100%" }} bodyStyle={{ height: "100%" }} title={t("Atividades em dia")}>
                      <Space wrap align="center" style={{ width: "100%", height: "80%", justifyContent: "center" }} size="large">
                        {progressItems.length ? (
                          progressItems.reverse().map(({ name, percent, total, emDia }) => (
                            <Tooltip
                              key={name}
                              title={
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                  <p>{`${t("Total:")} ${total}`}</p>
                                  <p>{`${t("Em dia:")} ${emDia}`}</p>
                                </div>
                              }
                            >
                              <Space key={name} direction="vertical" align="center" style={{ textAlign: "center" }}>
                                <Text strong>{t(name)}</Text>
                                <Progress
                                  type="dashboard"
                                  percent={percent.toFixed(1)}
                                  width={90}
                                  strokeColor={{
                                    "0%": "#108ee9",
                                    "100%": "#87d068",
                                  }}
                                />
                              </Space>
                            </Tooltip>
                          ))
                        ) : (
                          <Empty description={t("Não há dados para a data final selecionada")} />
                        )}
                      </Space>
                    </Card>
                  </div>
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={[8, 8]} className={styles["row-dashboard"]}>
        <Col span={24}>
          <Card title={t("Estatísticas de Armazenamento")} className={styles["card-chart"]}>
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              {/* GRAFICO DE DOCUMENTOS */}
              <Row gutter={[8, 8]}>
                <Col xs={24} lg={12}>
                  <Skeleton loading={dadosLoading} active paragraph={{ rows: 6, width: "100%" }}>
                    <div className={styles["line-chart-container"]}>
                      <LineChart tipoData="espaco" />
                    </div>
                  </Skeleton>
                </Col>

                <Col xs={24} lg={12}>
                  <Skeleton loading={dadosLoading} active paragraph={{ rows: 6, width: "100%" }}>
                    <div className={styles["line-chart-container"]}>
                      <LineChart tipoData="espaco" isArmazenamento />
                    </div>
                  </Skeleton>
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>
      </Row>

      {hasModulo("workflow") && (
        <Row gutter={[8, 8]} className={styles["row-dashboard"]}>
          <Col span={24}>
            <Card title={t("Estatísticas de Tarefas")} className={styles["card-chart"]}>
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                {/* GRÁFICO DE TAREFAS EM ABERTO */}
                <Row gutter={[8, 8]}>
                  <Col xs={24} lg={12}>
                    <Skeleton loading={dadosLoading} active paragraph={{ rows: 6, width: "100%" }}>
                      <div className={styles["line-chart-container"]}>
                        <LineChart tipoData="tarefas" />
                      </div>
                    </Skeleton>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Skeleton loading={dadosLoading} active paragraph={{ rows: 6, width: "100%" }}>
                      <div className={styles["line-chart-container"]}>
                        <LineChart tipoData="tarefasAbertas" />
                      </div>
                    </Skeleton>
                  </Col>
                </Row>

                <Row gutter={[8, 8]}>
                  <Col span={24}>
                    <Skeleton loading={workflowLoading} active paragraph={{ rows: 6, width: "100%" }}>
                      <div className={styles["mixed-barchart-container"]}>
                        <DashboardBarChart tipoGrafico="tarefas" />
                      </div>
                    </Skeleton>
                  </Col>
                  <Col span={24}>
                    <Skeleton loading={workflowLoading} active paragraph={{ rows: 6, width: "100%" }}>
                      <div className={styles["mixed-barchart-container"]}>
                        <DashboardBarChart tipoGrafico="tempo" />
                      </div>
                    </Skeleton>
                  </Col>
                </Row>
              </Space>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
}
